import { Button, IconButton } from '@vrplatform/components';
import {
  EmailIcon,
  GeneralIcon,
  PercentageCircleIcon,
  PhoneIcon,
  PinIcon,
  UserIcon,
} from '@vrplatform/icons';
import { Logo } from '@vrplatform/components-legacy';
import type { Maybe } from '@vrplatform/utils';
import {
  Anchor,
  Box,
  Card,
  Center,
  Group,
  Text as MText,
  Stack,
  rem,
} from '@mantine/core';
import { VRP_TENANT_ID } from '@vrplatform/ui-common';
import type { PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';
import { useListingDetailDrawer } from '../../../listings/drawer';
import { PlaceholderBadge as Badge } from './PlaceholderBagde';
import { useTeam } from '@vrplatform/data';

export type TenantInfo = {
  name: Maybe<string>;
  logo: Maybe<string>;
  email: Maybe<string>;
  phone: Maybe<string>;
  address: Maybe<string>;
  taxId: Maybe<string>;
};

type ListingInfo = {
  id: Maybe<string>;
  name: Maybe<string>;
  address: Maybe<string>;
};

type OwnerInfo = {
  companyName: Maybe<string>;
  name: Maybe<string>;
  email: Maybe<string>;
  address: Maybe<string>;
  phone: Maybe<string>;
};

type Props =
  | {
      mode: 'template';
      type: 'owner' | 'propertyManager';
      tenant: TenantInfo;
      showTeamEmail?: boolean;
      onShowTeamEmailChange?: (checked: boolean) => void;
    }
  | {
      mode: 'view';
      type: 'owner' | 'propertyManager';
      tenant: TenantInfo;
      showTeamEmail?: boolean;
      listing: Maybe<ListingInfo>;
      owner: Maybe<OwnerInfo>;
    };

export const StatementInfo = ({ tenant, ...props }: Props) => {
  const [{ isGL: GL }] = useTeam();
  const { open: openListingDetailDrawer } = useListingDetailDrawer();

  const iconSize = 18;
  const backgroundColor = '#ffffff40';
  const boxShadow = '0 2px 2px 0 #09090B05, 0 0 0 1px #09090B07';
  const isGlTeam = GL === true;
  const showTeamEmail = props.showTeamEmail ?? true;

  if (props.mode === 'template')
    return (
      <Card
        withBorder
        sx={(theme) => ({
          backgroundColor: isGlTeam ? theme.colors.neutral[0] : backgroundColor,
          borderColor: isGlTeam ? theme.colors.neutral[1] : undefined,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.lg,
          boxShadow: isGlTeam ? undefined : boxShadow,
        })}
      >
        <Group>
          <TeamLogo logo={tenant.logo} />
          <Box>
            <MText
              component="p"
              m={0}
              c="neutral.5"
              fz={rem(12)}
              lh={1.4}
              style={{
                letterSpacing: '0.1px',
              }}
            >
              Property Management
            </MText>
            <MText size="md" fw={500} component="p" m={0}>
              {tenant.name}
            </MText>
          </Box>
          <Button
            component={Link}
            to="/settings/team/general"
            sx={(theme) => ({
              marginLeft: 'auto',
              boxShadow: theme.shadows.xs,
            })}
          >
            Edit info
          </Button>
        </Group>

        <Stack mb="xl">
          <InputWrapper
            inputName={'address'}
            label={'Address'}
            icon={<PinIcon size={iconSize} />}
          >
            <TemplatePlaceholder
              value={tenant.address}
              viewType={props.type}
              placeholer="Add address"
            />
          </InputWrapper>

          <InputWrapper
            inputName="supportEmail"
            label="Email"
            icon={<EmailIcon size={iconSize} />}
            labelRight={
              <IconButton
                icon={showTeamEmail ? 'EyeIcon' : 'EyeSlashIcon'}
                onClick={() => props.onShowTeamEmailChange?.(!showTeamEmail)}
                tooltip={showTeamEmail ? 'Email is visible' : 'Email is hidden'}
              />
            }
          >
            <TemplatePlaceholder
              value={tenant.email}
              placeholer="Add email"
              viewType={props.type}
              opacity={!showTeamEmail ? 0.5 : undefined}
            />
          </InputWrapper>

          <InputWrapper
            inputName="supportPhone"
            label="Phone"
            icon={<PhoneIcon size={iconSize} />}
          >
            <TemplatePlaceholder
              value={tenant.phone}
              placeholer="Add phone"
              viewType={props.type}
            />
          </InputWrapper>

          <InputWrapper
            inputName="companyTaxCode"
            label="Tax ID"
            icon={<PercentageCircleIcon size={iconSize} />}
          >
            <TemplatePlaceholder
              value={tenant.taxId}
              placeholer="Add tax ID"
              viewType={props.type}
            />
          </InputWrapper>
        </Stack>

        <Badge>Listing name</Badge>
        <Stack>
          <InputWrapper label="Owner" icon={<UserIcon size={iconSize} />}>
            <Badge>Owner name</Badge>
          </InputWrapper>
          <InputWrapper
            label="Company"
            icon={<GeneralIcon size={iconSize - 1} />}
          >
            <Badge>Company name</Badge>
          </InputWrapper>

          <InputWrapper label="Address" icon={<PinIcon size={iconSize} />}>
            <Badge>Owner Address</Badge>
          </InputWrapper>
          <InputWrapper label="Email" icon={<EmailIcon size={iconSize} />}>
            <Badge>Owner email</Badge>
          </InputWrapper>
        </Stack>
      </Card>
    );

  const { listing, owner } = props;

  return (
    <Card
      withBorder
      radius="md"
      sx={(theme) => ({
        backgroundColor: isGlTeam ? theme.colors.neutral[0] : backgroundColor,
        borderColor: isGlTeam ? theme.colors.neutral[1] : undefined,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.lg,
        boxShadow: isGlTeam ? undefined : boxShadow,
      })}
    >
      <Group>
        <TeamLogo logo={tenant.logo} />
        <Box>
          <MText
            component="p"
            m={0}
            c="neutral.5"
            fz={rem(12)}
            lh={1.4}
            style={{
              letterSpacing: '0.1px',
            }}
          >
            Property Management
          </MText>
          <MText size="md" fw={500} component="p" m={0}>
            {tenant.name}
          </MText>
        </Box>
      </Group>

      <Stack mb="xl">
        {!tenant.address && props.type === 'owner' ? null : (
          <InputWrapper
            inputName={'address'}
            label={'Address'}
            icon={<PinIcon size={iconSize} />}
          >
            <TemplatePlaceholder
              value={tenant.address}
              placeholer="Add address"
              viewType={props.type}
            />
          </InputWrapper>
        )}

        {!showTeamEmail || (!tenant.email && props.type === 'owner') ? null : (
          <InputWrapper
            inputName="supportEmail"
            label="Email"
            icon={<EmailIcon size={iconSize} />}
          >
            <TemplatePlaceholder
              value={tenant.email}
              placeholer="Add email"
              viewType={props.type}
            />
          </InputWrapper>
        )}

        {!tenant.phone && props.type === 'owner' ? null : (
          <InputWrapper
            inputName="supportPhone"
            label="Phone"
            icon={<PhoneIcon size={iconSize} />}
          >
            <TemplatePlaceholder
              value={tenant.phone}
              placeholer="Add phone"
              viewType={props.type}
            />
          </InputWrapper>
        )}

        {!tenant.taxId && props.type === 'owner' ? null : (
          <InputWrapper
            inputName="companyTaxCode"
            label="Tax ID"
            icon={<PercentageCircleIcon size={iconSize} />}
          >
            <TemplatePlaceholder
              value={tenant.taxId}
              placeholer="Add tax ID"
              viewType={props.type}
            />
          </InputWrapper>
        )}
      </Stack>

      <Box>
        {listing?.name &&
          (props.type === 'propertyManager' && listing.id ? (
            <Anchor
              size="xs"
              component="button"
              m={0}
              onClick={() => openListingDetailDrawer(listing.id!)}
            >
              {listing?.name}
            </Anchor>
          ) : (
            <MText size={'xs'} fw={400} component="p" c="gray" m={0}>
              {listing?.name}
            </MText>
          ))}

        {listing?.address && (
          <MText size="md" fw={500} component="p" m={0}>
            {listing?.address}
          </MText>
        )}
      </Box>

      {!owner ? (
        <Badge>Missing owner</Badge>
      ) : (
        <Stack>
          {owner?.companyName && (
            <InputWrapper
              label="Company"
              icon={<GeneralIcon size={iconSize - 1} />}
            >
              <Text>{owner?.companyName}</Text>
            </InputWrapper>
          )}
          {owner?.name && (
            <InputWrapper label="Owner" icon={<UserIcon size={iconSize} />}>
              <Text>{owner.name}</Text>
            </InputWrapper>
          )}

          {owner?.address && (
            <InputWrapper
              inputName={'address'}
              label={'Address'}
              icon={<PinIcon size={iconSize} />}
            >
              <Text>{owner?.address}</Text>
            </InputWrapper>
          )}
          {owner?.phone && (
            <InputWrapper label="Phone" icon={<PhoneIcon size={iconSize} />}>
              <Text>{owner?.phone}</Text>
            </InputWrapper>
          )}
          {owner?.email && (
            <InputWrapper label="Email" icon={<EmailIcon size={iconSize} />}>
              <Text>{owner?.email}</Text>
            </InputWrapper>
          )}
        </Stack>
      )}
    </Card>
  );
};

const Text = ({
  children,
  opacity,
}: {
  children: React.ReactNode;
  opacity?: number;
}) => (
  <MText component="p" m={0} size="sm" opacity={opacity}>
    {children}
  </MText>
);

const TemplatePlaceholder = ({
  placeholer,
  value,
  viewType,
  opacity,
}: {
  value: Maybe<string>;
  placeholer: string;
  viewType: Props['type'];
  opacity?: number;
}) => {
  if (value) return <Text opacity={opacity}> {value}</Text>;
  if (viewType === 'owner') return null;

  return (
    <Anchor
      size="sm"
      m={0}
      component={Link}
      to="/settings/team/general"
      opacity={opacity}
    >
      {placeholer}
    </Anchor>
  );
};

type InputWrapperProps = {
  icon: React.ReactNode;
  label?: string;
  inputName?: string;
  labelRight?: React.ReactNode;
};

const InputWrapper = ({
  icon,
  children,
  label,
  inputName,
  labelRight,
}: PropsWithChildren<InputWrapperProps>) => {
  return (
    <Group gap={'xs'} wrap="nowrap">
      <Center sx={{ flexShrink: 0 }}>{icon}</Center>
      <MText
        size="sm"
        fw={500}
        component={inputName ? 'label' : 'span'}
        htmlFor={inputName}
        m={0}
        miw={100}
      >
        {label}
      </MText>
      {children}
      {labelRight}
    </Group>
  );
};

const TeamLogo = ({ logo }: { logo: Maybe<string> }) => {
  const [{ id: teamId }] = useTeam();
  const isVrp = teamId === VRP_TENANT_ID;

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${theme.colors.gray[2]}`,
        borderRadius: theme.radius.md,
        backgroundColor: theme.white,
        overflow: 'hidden',
        padding: rem(4),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '55px',
      })}
    >
      <Logo img={isVrp ? undefined : logo} width={40} />
    </Box>
  );
};
